import React from 'react';
import ThreeDots from '../ui/ThreeDots';
import { getTimeAgo } from '../../utility/getTimeAgo';
import StyledCard from './StyledCard';
import CustomBadge from '../Badge/CustomBadge';
import { Building, Building2, Copy, Pin, Rss } from 'lucide-react';
import CustomToolTip from '../Tooltip/CustomToolTip';
import { copyToClipboard } from '../../utility/CopyToClipboard';
import { useLocation } from 'react-router-dom';
import { showSuccessToast } from '../ui/Toast';
import IconWrapper from './IconWrapper';
import { ClockArrowUp, /* DatabaseZap, */ SignalHigh } from 'lucide-react';
import { hasPermission, PERMISSIONS } from '../../config/permissions.config';
import { useClosedBadge } from '../../context/ThemeContext';

// Helper function to truncate text to specific number of words
const truncateWords = (text, wordLimit) => {
  if (!text) return '';
  const words = text.replace("<br>","").split(' ');
  if (words.length > wordLimit) {
    return words.slice(0, wordLimit).join(' ') + '...';
  }
  return text;
};

const JobDetailItem = ({ icon: Icon, text }) => (
  <div className="flex gap-2 items-center w-fit md:max-w-[15%]">
    <Icon />
    <p className="typography-body w-full max-w-[8rem] whitespace-nowrap text-ellipsis overflow-hidden">{text}</p>
  </div>
);

const JobFooterItem = ({ label, value }) => (
  <div className="flex flex-col ">
    <span className="typography-small-p text-font-gray mb-[2px]">{label}</span>
    <span className="typography-body">{value}</span>
  </div>
);

const JobCard = ({
  job,
  handleAction,
  page,
  onClick,
  isApplied,
  withKebab,
  isAdmin,
  isCandidate,
  isAuthenticatedCandidate,
  application, // Receive the application prop
  role = "Candidate", 
  pinnedJobs = []
}) => {
  const formattedCreatedAt = getTimeAgo(job.createdAt);
  const formattedAppliedAt = getTimeAgo(job.applicationDate);

  const location = useLocation();

  // Hidden for now. Uncomment with the pay row below to show the job budget again.
  // const isHourlyRateJob = job.employmentType === 'Part Time' || job.employmentType === 'Contract';
  // const payUnit = isHourlyRateJob ? 'INR/hr' : 'LPA';

  // Initialize variables for application data
  let applicationDate = null;
  let currentStage = null;
  let currentStageStatus = null;

  if (isAuthenticatedCandidate && application) {
    applicationDate = application.applicationDate;
    currentStage = application.currentStage;
    currentStageStatus =
      application.stageStatuses &&
      application.currentStage &&
      application.stageStatuses[application.currentStage] &&
      application.stageStatuses[application.currentStage].status
        ? application.stageStatuses[application.currentStage].status
        : 'N/A';
  }

  const handleCardClick = () => {
    if (onClick) onClick(job._id);
  };

  const adminFooterItems = [
    { label: 'Candidates', value: `${job.applied || 0} Applied` },
    { label: 'Engagement', value: `${job.applyClickCount || 0} Clicks` },
    { label: 'Applications', value: `${job.processed || 0} Processed` },
    { label: 'Posted', value: formattedCreatedAt },
  ];

  let candidateFooterItems = [];

  if (isAuthenticatedCandidate) {
    candidateFooterItems = [
      {
        label: 'Applied on',
        value: applicationDate
          ? formattedAppliedAt
          : 'N/A',
      },
      {
        label: 'Current Stage',
        value: currentStage || 'N/A',
      },
      {
        label: 'Stage Status',
        value: currentStageStatus || 'N/A',
      },
    ];
  } else if (isCandidate) {
    candidateFooterItems = [
      {
        label: 'Posted',
        value: formattedCreatedAt,
      },
      {
        label: 'Workplace Type',
      value: job.workplaceType,
      },
    ];
  }
  // Truncate job description to 10 words
  const truncatedDescription = truncateWords(job.jobDescription || 'No description available', 40);

  const handleCopyLink = async (e,jobId,company_id) => {
    e.stopPropagation()
    await copyToClipboard(`${window.location.origin}/org/${company_id}/view-job/${jobId}`)
    showSuccessToast("Success","Job link copied successfuully")
  }

  const ClosedBadgeComponent = useClosedBadge()

  return (
    <StyledCard
    padding={0}
    extraStyles={(onClick ? 'cursor-pointer' : '' ) + ` hover:outline-accent-100 hover:outline hover:outline-2  group relative`}
    onClick={onClick ? handleCardClick : undefined}
  >
    <StyledCard padding={2} backgroundColor={'bg-background-100'} borderRadius={'  rounded-t-xl '} extraStyles={' transition-colors !pb-0 duration-200 '}>
      <div className='flex flex-col md:flex-row items-start justify-between gap-4'>
        <h3 className={`group-hover:text-font-accent w-full min-w-0 whitespace-normal break-words md:whitespace-nowrap md:overflow-hidden md:text-ellipsis ${
          isApplied ? 'md:w-[50%] xl:w-[60%]' : 'md:w-[60%]'
        }`}>
          {job.jobTitle}
        </h3>
        <div className={`flex items-center gap-3 flex-wrap shrink-0 ${isApplied ? 'md:mb-0' : ''}`}>
          {isApplied &&         
          <span className="bg-blue-300 text-blue-100 typography-body px-4 py-2 rounded-xl">
            Applied
          </span>}
          {hasPermission(role,PERMISSIONS.SHOW_JOBCARD_MANAGEMENT_OPTION) && job?.status === 'open' && 
          <div className='flex items-center gap-4'>
            {
              pinnedJobs?.includes(job?._id) && 
              <div className='rotate-45'>
                <IconWrapper icon={Pin} size={0} isInActiveIcon />
              </div>
            }
            <CustomToolTip arrowed title={"Copy link"}>
              <div onClick={(e) => handleCopyLink(e,job?._id , job?.company_id)} className='cursor-pointer p-2 h-10 flex justify-center items-center bg-background-80 hover:bg-background-70 rounded-xl'>
                <Copy size={20} strokeWidth={1} color='#808389'/>            
              </div>
            </CustomToolTip>
          </div>}
          {(job.status !== "deleted" && job.status !== "closed") &&
            <CustomBadge extraStyles={'whitespace-nowrap md:whitespace-normal'} label={job?.jobProfile} paddingX={2} />}
          {withKebab && (
            <ThreeDots job={job} customBgColor={'bg-background-80'}  handleAction={handleAction} extraStyles={'z-10'} page={page} isPinned={pinnedJobs?.includes(job?._id)} role={role} />
          )}
        </div>
      </div>
      {job.status !== "deleted" ?
      <>
      <div className="flex flex-row items-start flex-wrap gap-4 mt-2">
        <JobDetailItem icon={() => <IconWrapper size={1} icon={ClockArrowUp}  isInActiveIcon />} text={job.employmentType} />
        {/* Hidden for now. Uncomment to show the job pay range again.
        {((job.budgetTo > 1) || (job.budgetFrom > 0)) && 
        <JobDetailItem
          icon={() => <IconWrapper size={1} icon={DatabaseZap}  isInActiveIcon />}
          text={`${job.budgetFrom} - ${job.budgetTo} ${payUnit}`}
        />}
        */}
        <JobDetailItem
          icon={() => <IconWrapper size={1} icon={SignalHigh}  isInActiveIcon />}
          text={`${job.experienceFrom} - ${job.experienceTo} Year`}
        />
        {hasPermission(role,PERMISSIONS.SHOW_JOBCARD_COMPANY_NAME) && (job?.companyDetails?.name || job?.company_id?.name ) && <JobDetailItem
        icon={() => <IconWrapper size={1} icon={Building2}  isInActiveIcon />}
        text={job?.companyDetails?.name || job?.company_id?.name }
        />}
        {hasPermission(role,PERMISSIONS.SHOW_JOBCARD_JOB_IS_PUBLIC) && (job?.isPublic ) && <JobDetailItem
        icon={() => <IconWrapper size={1} icon={Rss}  isInActiveIcon />}
        text={"Open to All"}
        />}
      </div>
      <div className=" py-4 ">
          <p className="typography-body text-font-gray h-[2.25rem] overflow-hidden" dangerouslySetInnerHTML={{__html : truncatedDescription}}>
          </p>
        </div>
      </> :
      <div className=" py-4 ">
      <p className="typography-body text-font-gray h-[2.25rem] overflow-hidden" dangerouslySetInnerHTML={{__html : truncatedDescription}}>
      </p>
    </div>
        }
    {(job.status === 'closed' || job.status === 'deleted' ) && (
    <div className="flex  pb-4 w-fit">
      <p className="typography-body text-font-gray mr-2 whitespace-nowrap">
        Closure Reason :
      </p>
      <p className="typography-body">{job.closingReason || 'N/A'}</p>
    </div>
    )}
    </StyledCard>
    
  
    {(isAdmin || isCandidate) && (
      <StyledCard padding={2} borderRadius={' rounded-b-xl'} backgroundColor={'bg-background-60'} extraStyles="flex items-center justify-between !pt-4 !pb-4">
        <div className="flex justify-between w-full md:justify-start gap-3 md:gap-8">
          {(isAdmin ? adminFooterItems : candidateFooterItems).map(
            (item, index) => (
              <JobFooterItem
                key={index}
                label={item.label}
                value={item.value}
              />
            )
          )}
        </div>
        {(job.status === 'closed' || job.status === 'deleted' ) && (
        <div className="flex p-4 w-fit">
          <p className="typography-body text-font-gray mr-2 whitespace-nowrap">
            This job post has been closed 
          </p>
          <div className='absolute top-10 z-0 right-10'>
            <ClosedBadgeComponent />
          </div>
        </div>
        )}
      </StyledCard>
    )}
  </StyledCard>
  
  
  );
};

export default JobCard;